import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { setDemoSession } from '../lib/session';

// Split-screen panes: the slot (and the "land already live" hint) must survive
// this entry's own redirect, or the pane silently falls back to the shared
// session keys. Only these two params are carried, and only as given.
const carryPaneParams = (target) => {
    const p = new URLSearchParams(window.location.search);
    const keep = new URLSearchParams();
    ['demo_slot', 'live'].forEach((key) => {
        const v = p.get(key);
        if (v && /^[\w-]{1,24}$/.test(v)) keep.set(key, v);
    });
    const qs = keep.toString();
    if (!qs) return target;
    return target.includes('?') ? `${target}&${qs}` : `${target}?${qs}`;
};


// Automotive COMMAND CENTER entry — no-login. Mints the isolated automotive demo
// MANAGER session (view_command_center) and lands in the Command Center. Mirrors
// the property demo entry: immediate dark theme (no white flash) + return_url
// handling for a deterministic Back. On failure it shows a VISIBLE error card
// (never a silent /login eject).
export default function DemoAutoCommandCenterEntry() {
    const { t } = useTranslation();
    const [error, setError] = useState(null);

    const enter = async () => {
        setError(null);
        const API = (typeof window !== 'undefined' ? window.location.origin : '');
        // Force dark/night immediately so there is no white flash on a projector.
        try { localStorage.setItem('yulontap-theme-preference', 'dark'); } catch (_e) { /* ignore */ }
        document.documentElement.classList.add('dark');
        const params = new URLSearchParams(window.location.search);
        const rawReturn = params.get('return_url') || '';
        // Only relative in-app paths are honoured for the demo Back hint (guards
        // against open-redirect); off-site/absolute URLs are ignored.
        const returnUrl = rawReturn.startsWith('/') && !rawReturn.startsWith('//') ? rawReturn : '';
        try {
            const qs = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : '';
            const res = await fetch(`${API}/api/demo-auto/manager/session${qs}`);
            let d = {};
            try { d = await res.json(); } catch (_e) { /* non-JSON body */ }
            if (!res.ok || !d.access_token) {
                throw new Error(`HTTP ${res.status}${d.detail ? ` — ${d.detail}` : ''}`);
            }
            setDemoSession(d.access_token, d.user || {});
            sessionStorage.setItem('yulontap_demo_mode', '1');
            sessionStorage.setItem('yulontap_auto_demo', '1');
            sessionStorage.removeItem('yulontap_hotel_demo');
            if (returnUrl) sessionStorage.setItem('yulontap_demo_return', returnUrl);
            else sessionStorage.removeItem('yulontap_demo_return');
            window.location.replace(carryPaneParams('/command-center?org=auto-demo-org-0001'));
        } catch (e) {
            setError(e.message || 'Network error');
        }
    };

    useEffect(() => { enter(); }, []);

    if (error) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-950 text-slate-200 px-6 text-center"
                data-testid="demo-auto-command-center-error">
                <AlertTriangle className="w-9 h-9 text-amber-400" />
                <p className="text-base font-semibold text-white">{t('demoAuto.entryFailedTitleCc', 'Could not open the Command Center demo')}</p>
                <p className="text-sm text-slate-400 max-w-md">{t('demoAuto.entryFailedBody', 'The demo session could not be created. This is not a login problem — the details below show what failed.')}</p>
                <code className="text-xs text-rose-300 bg-black/40 rounded px-3 py-1.5" data-testid="demo-auto-command-center-error-detail">{error}</code>
                <button onClick={enter} data-testid="demo-auto-command-center-retry"
                    className="mt-2 inline-flex items-center gap-2 text-sm text-white bg-amber-600 hover:bg-amber-500 rounded-full px-4 py-2 transition-colors">
                    <RefreshCw className="w-4 h-4" />{t('demoAuto.entryRetry', 'Try again')}
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-950 text-slate-200"
            data-testid="demo-auto-command-center-entry">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
            <p className="text-sm">{t('demoAuto.enteringCommandCenter', 'Opening Command Center…')}</p>
        </div>
    );
}
